<template>
  <div v-show="isHide" v-loading="loading">
    <el-row class="header-row">
      <el-col :span="24" class="header-col">
        <label v-if="hasLabel">{{ data.label || 'Address' }}</label>

        <el-tooltip :content="editTooltipText" placement="top">
          <i :class="[editIcon, 'edit-view-icon']" @click="openDrawer"></i>
        </el-tooltip>

        <el-tooltip content="Clear address" placement="top">
          <i
            v-if="form[data.key] && !isView && !readonly()"
            class="el-icon-circle-close clear-icon-top"
            @click.stop="clearAddress"
          ></i>
        </el-tooltip>
      </el-col>
    </el-row>

    <div class="address-compact" :class="{ clickable: !isView, bordered: !isView }">
      <div class="address-text">
        <span v-if="form[data.key]">{{ form[data.key] }}</span>
        <span v-else>-</span>
      </div>
    </div>

    <el-drawer
      title="Address Details"
      :visible.sync="drawerVisible"
      size="70%"
      append-to-body
      :destroy-on-close="true"
      :with-header="true"
      :wrapper-style="{ zIndex: 9999 }"
      :before-close="handleBeforeClose"
    >
      <div class="drawer-content">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-row v-for="subField in leftFields" :key="subField.key" style="margin-bottom: 12px">
              <el-col :span="24">
                <label v-if="subFieldLabels[subField.key]">
                  {{ subFieldLabels[subField.key] }}
                  <span v-if="isRequired(subField.key)" class="required">*</span>
                </label>
                <div v-if="isView || readonly()">
                  <span>{{ address_data[subField.key] || '-' }}</span>
                </div>
                <div v-else>
                  <component
                    :is="getFieldComponent(subField)"
                    v-model="address_data[subField.key]"
                    :options="getDropdownOptions(subField.key)"
                  />
                </div>
              </el-col>
            </el-row>
          </el-col>

          <el-col :span="12">
            <el-row v-for="subField in rightFields" :key="subField.key" style="margin-bottom: 12px">
              <el-col :span="24">
                <label v-if="subFieldLabels[subField.key]">
                  {{ subFieldLabels[subField.key] }}
                  <span v-if="isRequired(subField.key)" class="required">*</span>
                </label>
                <div v-if="isView || readonly()">
                  <span>{{ address_data[subField.key] || '-' }}</span>
                </div>
                <div v-else>
                  <component
                    :is="getFieldComponent(subField)"
                    v-model="address_data[subField.key]"
                    :options="getDropdownOptions(subField.key)"
                  />
                </div>
              </el-col>
            </el-row>
          </el-col>
        </el-row>
      </div>

      <div class="drawer-footer">
        <el-button @click="handleBeforeClose">Close</el-button>
        <el-button v-if="!isView && !readonly()" type="primary" @click="saveAddress">
          Save
        </el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
  export default {
    name: 'AddressExecuteField',
    props: ['data', 'label', 'isView', 'hasLabel', 'checkIsDisabled', 'form'],
    data() {
      return {
        drawerVisible: false,
        loading: false,
        address_data: {},
        subFieldSchema: [
          { key: 'name', label: 'Name', type: 'text' },
          { key: 'address', label: 'Street Address', type: 'text' },
          { key: 'city', label: 'City', type: 'text' },
          { key: 'state', label: 'State/Province', type: 'text' },
          { key: 'zip', label: 'Postal Code', type: 'text' },
          { key: 'country', label: 'Country', type: 'text' },
          { key: 'mobile', label: 'Mobile Number', type: 'text' },
          { key: 'fax', label: 'Fax Number', type: 'text' },
          { key: 'email', label: 'Email', type: 'text' },
        ],
      };
    },
    computed: {
      isHide() {
        return !(this.data?.properties?.filed_content === 'Hide');
      },
      editTooltipText() {
        if (this.isView || this.readonly()) return 'View address';
        return this.drawerVisible ? 'Close address editor' : 'Edit address';
      },
      editIcon() {
        if (this.isView || this.readonly()) return 'el-icon-view';
        return this.drawerVisible ? 'el-icon-close' : 'el-icon-edit';
      },
      subFieldLabels() {
        return this.subFieldSchema.reduce((map, field) => {
          map[field.key] = field.label;
          return map;
        }, {});
      },
      leftFields() {
        return ['name', 'address', 'city', 'state', 'zip', 'country'].map((key) =>
          this.subFieldSchema.find((f) => f.key === key)
        );
      },
      rightFields() {
        return ['mobile', 'fax', 'email'].map((key) =>
          this.subFieldSchema.find((f) => f.key === key)
        );
      },
    },
    methods: {
      openDrawer() {
        this.address_data = { ...(this.form[this.data.key + '/values'] || {}) };
        this.drawerVisible = true;
      },
      minimalDisplay() {
        const values = this.form[this.data.key + '/values'];
        if (!values) return '';
        return (this.data.address.minimal_subfields || [])
          .map((key) => values[key])
          .filter(Boolean)
          .join(', ');
      },
      isDirty() {
        const original = this.form[this.data.key + '/values'] || {};
        return JSON.stringify(original) !== JSON.stringify(this.address_data);
      },
      saveAddress() {
        const required = this.data.address?.required_subfields || [];
        const missing = required.filter((key) => !this.address_data[key]);

        if (missing.length) {
          this.$message.error(
            'Please fill required fields: ' +
              missing.map((f) => this.subFieldLabels[f] || f).join(', ')
          );
          return false;
        }

        this.form[this.data.key + '/values'] = { ...this.address_data };
        this.form[this.data.key] = this.minimalDisplay();
        this.drawerVisible = false;
        return true;
      },
      clearAddress() {
        this.$confirm(`Do you want to clear ${this.data.label}?`, 'Confirm', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'warning',
        })
          .then(() => {
            this.address_data = {};
            this.form[this.data.key + '/values'] = {};
            this.form[this.data.key] = '';
            this.$message.success('Address cleared');
          })
          .catch(() => {});
      },
      async handleBeforeClose(done) {
        if (!this.isDirty()) {
          this.drawerVisible = false;
          done && done();
          return;
        }

        try {
          const confirm = await this.$confirm(
            'Do you want to save changes before closing?',
            'Confirm',
            {
              confirmButtonText: 'Save & Close',
              cancelButtonText: 'Discard',
              distinguishCancelAndClose: true,
              type: 'warning',
            }
          );

          if (confirm === 'confirm') {
            if (this.saveAddress()) done && done();
          }
        } catch (action) {
          if (action === 'cancel') {
            this.drawerVisible = false;
            done && done();
          }
        }
      },
      isRequired(key) {
        return this.data.address?.required_subfields?.includes(key);
      },
      readonly() {
        if (this.data.field_assignable === 'read_only' && this.data.is_entity_variable) {
          return true;
        }
        if (
          this.data &&
          this.data.properties &&
          this.data.properties.filed_content === 'READ ONLY'
        ) {
          return true;
        }
      },
      getDropdownOptions(key) {
        if (key === 'country') {
          return [
            { label: 'India', value: 'IN' },
            { label: 'USA', value: 'US' },
            { label: 'UK', value: 'UK' },
          ];
        }
        return [];
      },
      getFieldComponent(field) {
        switch (field.type) {
          case 'toggle':
            return 'el-switch';
          case 'dropdown':
            return 'el-select';
          default:
            return 'el-input';
        }
      },
    },
  };
</script>

<style scoped>
  .header-row {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  .header-col {
    display: flex;
    align-items: center;
  }

  .edit-view-icon {
    font-size: 16px;
    cursor: pointer;
    margin-left: 10px;
    transition: color 0.2s;
  }

  .edit-view-icon:hover {
    color: #66b1ff;
  }

  .clear-icon-top {
    margin-left: auto;
    font-size: 14px;
    padding-left: 8px;
    cursor: pointer;
  }

  .clear-icon-top:hover {
    color: #f56c6c;
  }

  .address-compact {
    position: relative;
    display: flex;
    align-items: center;
    padding: 3px;
    border-radius: 4px;
    font-weight: 500;
    min-height: 36px;
  }

  .address-compact.bordered {
    border: 1px solid #e0e0e0;
  }

  .address-text {
    flex: 1;
    max-height: 65px;
    overflow-y: auto;
    word-break: break-word;
    white-space: pre-wrap;
    padding-right: 2px;
    cursor: pointer; /* <-- Add this line */
  }

  /* Custom scrollbar */
  .address-text::-webkit-scrollbar {
    width: 6px;
    cursor: pointer; /* <-- Optional: Not supported in all browsers */
  }

  .address-text::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 3px;
    cursor: pointer; /* <-- Add this line */
  }

  .address-text::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
    cursor: pointer; /* <-- Add this line */
  }

  .required {
    color: red;
  }

  .drawer-content {
    padding: 16px;
    padding-bottom: 70px;
    max-height: calc(100% - 70px);
    overflow-y: auto;
  }

  .drawer-footer {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: 10px 16px;
    background: #fff;
    border-top: 1px solid #ebeef5;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
</style>
